import { useEffect, useRef } from "preact/hooks";
import { bootStatus } from "../state/boot";
import { gameStatus } from "../state/game";
import { handleEscape, hasEscapeHandler, screen } from "../state/route";
import type { Screen } from "../state/route";
import { ToastHost } from "../ui/components/Toast";
import { useArrowNav } from "../ui/hooks/useArrowNav";
import { CreateLobby } from "../ui/screens/CreateLobby";
import { Game } from "../ui/screens/Game";
import { Loadouts } from "../ui/screens/Loadouts";
import { MainMenu } from "../ui/screens/MainMenu";
import { Play } from "../ui/screens/Play";
import { Profile } from "../ui/screens/Profile";
import { Ranked } from "../ui/screens/Ranked";
import { ServerBrowser } from "../ui/screens/ServerBrowser";
import { SettingsScreen } from "../ui/screens/SettingsScreen";
import { BootError, BootLoading, NamePrompt } from "./BootScreens";

/** Controls that take typed text (Escape should leave them before it navigates). */
const TEXT_ENTRY = "textarea, input:not([type=checkbox]):not([type=radio]):not([type=range]):not([type=color])";

function ScreenView({ name }: { name: Screen }) {
  switch (name) {
    case "main":
      return <MainMenu />;
    case "play":
      return <Play />;
    case "ranked":
      return <Ranked />;
    case "browser":
      return <ServerBrowser />;
    case "create":
      return <CreateLobby />;
    case "loadouts":
      return <Loadouts />;
    case "profile":
      return <Profile />;
    case "settings":
      return <SettingsScreen />;
    case "game":
      return <Game />;
  }
}

function BootView() {
  switch (bootStatus.value) {
    case "name":
      return <NamePrompt />;
    case "error":
      return <BootError />;
    default:
      return <BootLoading />;
  }
}

/**
 * Menu overlay root. Renders the boot screens until the session and catalog are ready, then the
 * current screen of the route stack. While a match is being played it renders only the toasts so
 * the canvas and the HUD receive all input.
 */
export function App() {
  const root = useRef<HTMLDivElement>(null);
  const ready = bootStatus.value === "ready";
  const playing = ready && screen.value === "game" && gameStatus.value === "playing";

  useArrowNav(root);

  useEffect(() => {
    // Escape = back. The game session owns Escape while playing (pause / pointer lock).
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || e.isComposing) return;
      if (gameStatus.value === "playing") return;
      // In a text field the first Escape leaves the field, the next one goes back.
      const active = document.activeElement;
      if (!hasEscapeHandler() && active instanceof HTMLElement && active.matches(TEXT_ENTRY)) {
        active.blur();
        e.preventDefault();
        return;
      }
      if (handleEscape()) e.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <>
      {/* The root stays mounted so the arrow-navigation listener survives a match. */}
      <div ref={root} class={playing ? "app-root app-root--hidden" : "app-root"}>
        {!playing && (
          <>
            <div class="backdrop" aria-hidden="true" />
            <main class="stage">{ready ? <ScreenView name={screen.value} /> : <BootView />}</main>
          </>
        )}
      </div>
      <ToastHost />
    </>
  );
}
